/* global ReSourceRemoteLogging ReSourceRemoteLoggingEndpoint CSRFMiddlewareToken UserData DyadData PairingData */

import jsan from 'jsan';
import Logger from 'js-logger';
import {createAction} from 'redux-actions';

import ReSourceAPI from './api';
import fetch from './fetch';
import {iOSApp} from './mobileDetection';

Logger.useDefaults({
    defaultLevel: Logger.DEBUG,
});

const consoleHandler = Logger.createDefaultHandler();

/*
 * The iOS app sends every console line of its web view to Sentry as an event of
 * its own. So inside the iOS app the levels below ERROR do not go to the console
 * but become breadcrumbs of the page's Sentry, the same trail the Sentry SDK
 * records from the console everywhere else. ERROR still goes to the console.
 */
const breadcrumbHelpers = {
    [Logger.DEBUG.name]: 'addBreadcrumbDebug',
    [Logger.INFO.name]: 'addBreadcrumbInfo',
    [Logger.WARN.name]: 'addBreadcrumbWarn',
};

const BREADCRUMB_ARGUMENT_LENGTH = 500;
const BREADCRUMB_MESSAGE_LENGTH = 1000;

const truncate = (text, length) => (text.length > length ? `${text.slice(0, length)}...` : text);

const describeArgument = argument => {
    if (typeof argument === 'string') return argument;
    if (typeof argument === 'function') return `[Function: ${argument.name || '<anonymous>'}]`;
    if (argument instanceof Error) return `${argument.name}: ${argument.message}`;

    let value = argument;

    // JSON.stringify only sees {"isTrusted":true} of an event, the close code and reason are getters.
    if (typeof Event !== 'undefined' && argument instanceof Event) {
        value = {type: argument.type};
        ['code', 'reason', 'wasClean', 'message'].forEach(key => {
            if (key in argument) value[key] = argument[key];
        });
    }

    try {
        const json = JSON.stringify(value);
        if (json !== undefined) return json;
    } catch (error) {
        // circular or otherwise not serializable
    }

    try {
        return String(argument);
    } catch (error) {
        return '[unserializable]';
    }
};

const describeMessages = (messages, name) => {
    const parts = Array.prototype.map.call(messages, argument => (
        truncate(describeArgument(argument), BREADCRUMB_ARGUMENT_LENGTH)
    ));

    if (name) parts.unshift(`[${name}]`);

    return truncate(parts.join(' '), BREADCRUMB_MESSAGE_LENGTH);
};

/**
 * Inside the iOS app nothing is written to the console, not even when Sentry is
 * missing: the console would send it to the app's Sentry again.
 */
const addConsoleBreadcrumb = (level, messages, name) => {
    const sentry = window.Sentry;

    if (!sentry || !sentry.initialized) return;

    const helper = sentry[breadcrumbHelpers[level.name]] || sentry.addBreadcrumbInfo;

    try {
        helper('console', describeMessages(messages, name));
    } catch (error) {
        // logging must never break the page
    }
};

Logger.setHandler((messages, context) => {
    if (iOSApp && breadcrumbHelpers[context.level.name]) {
        addConsoleBreadcrumb(context.level, messages, context.name);
    } else {
        consoleHandler(messages, context);
    }

    if (ReSourceRemoteLogging === false) return;

    const [...message] = messages;
    const now = new Date();
    const data = {
        level: context.level.name,
        message: JSON.stringify(message),
        timestamp: now.toISOString(),
    };

    if (context.name) data.name = context.name;

    const request = fetch(ReSourceRemoteLoggingEndpoint, {
        method: 'POST',
        body: data,
        credentials: 'include',
        headers: {
            'X-CSRFToken': CSRFMiddlewareToken,
        },
    });

    request.catch(error => {
        if (iOSApp) {
            addConsoleBreadcrumb(Logger.INFO, ['Error sending log to server:', error]);
        } else {
            console.log('Error sending log to server:', error);
        }
    });
});

export {Logger};

const storeLogger = Logger.get('js.redux');

/**
 * Redux logging middleware
 *
 * Logs every action dispatched over the redux store
 */
const loggingMiddleware = () => next => action => {
    const {type, payload} = action;

    storeLogger.debug(`Store received action of type ${type} with payload:`, payload);

    return next(action);
};

const REDUX_LOGGER_ACTION = 'logger.redux';
const logToServer = createAction(REDUX_LOGGER_ACTION, message => ({message}));

const createLoggingMiddleware = ({initialState, csrfToken}) => {
    const api = new ReSourceAPI(csrfToken);

    const payload = [];

    return () => next => action => {
        if (action.type === REDUX_LOGGER_ACTION) {
            const {message} = action.payload;

            const body = {
                message,
                UserData,
                DyadData,
                PairingData,
                body: {
                    payload,
                    preloadedState: initialState,
                },
            };

            api.logs.create(jsan.stringify(body, null, null, true));

            return next(action);
        }

        payload.push(jsan.stringify(action));

        return next(action);
    };
};


export {
    loggingMiddleware,
    createLoggingMiddleware,
    logToServer,
};



// WEBPACK FOOTER //
// ./scripts/utils/logging.js